import React, { createContext, useContext, useReducer, useEffect, useMemo, useCallback } from 'react';
import { useToast } from './ToastContext';

const AuthContext = createContext();

const initialState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case 'LOGIN_ERROR':
    case 'REGISTER_ERROR':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    
    case 'TOKEN_REFRESH_SUCCESS':
      return {
        ...state,
        tokens: action.payload,
        error: null
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumpbackend.urion.live/api';

  // Load user from localStorage on app start
  useEffect(() => {
    const loadUser = async () => {
      const tokens = localStorage.getItem('tokens');
      
      if (tokens) {
        try {
          const parsedTokens = JSON.parse(tokens);
          
          // Validate token by fetching user profile
          const response = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${parsedTokens.access_token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: data.data.user,
                tokens: parsedTokens
              }
            });
          } else {
            // Token invalid, try to refresh
            await refreshToken();
          }
        } catch (error) {
          localStorage.removeItem('tokens');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadUser();
  }, []);

  const login = useCallback(async (username, password) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('tokens', JSON.stringify(data.data.tokens));
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: data.data
        });
        return { success: true };
      } else {
        dispatch({
          type: 'LOGIN_ERROR',
          payload: data.error?.message || 'Login failed'
        });
        return { success: false, error: data.error?.message || 'Login failed' };
      }
    } catch (error) {
      const errorMessage = 'Network error. Please try again.';
      dispatch({
        type: 'LOGIN_ERROR',
        payload: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  }, [API_BASE_URL]);

  const register = useCallback(async (username, email, password) => {
    dispatch({ type: 'REGISTER_START' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, email, password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('tokens', JSON.stringify(data.data.tokens));
        dispatch({
          type: 'REGISTER_SUCCESS',
          payload: data.data
        });
        return { success: true };
      } else {
        dispatch({
          type: 'REGISTER_ERROR',
          payload: data.error?.message || 'Registration failed'
        });
        return { success: false, error: data.error?.message || 'Registration failed' };
      }
    } catch (error) {
      const errorMessage = 'Network error. Please try again.';
      dispatch({
        type: 'REGISTER_ERROR',
        payload: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  }, [API_BASE_URL]);

  const logout = useCallback(async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: tokens.refresh_token })
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('tokens');
      // Clear wallet data from localStorage on logout
      localStorage.removeItem('safepump_active_wallet');
      dispatch({ type: 'LOGOUT' });
    }
  }, [API_BASE_URL]);

  const refreshToken = useCallback(async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      
      if (!tokens.refresh_token) {
        throw new Error('No refresh token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: tokens.refresh_token })
      });
      
      if (response.ok) {
        const data = await response.json();
        const newTokens = data.data;
        
        localStorage.setItem('tokens', JSON.stringify(newTokens));
        dispatch({
          type: 'TOKEN_REFRESH_SUCCESS',
          payload: newTokens
        });
        
        return newTokens;
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      localStorage.removeItem('tokens');
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  }, [API_BASE_URL]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Memoize the value object to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user: state.user,
    tokens: state.tokens,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    login,
    register,
    logout,
    refreshToken,
    clearError
  }), [state.user, state.tokens, state.isAuthenticated, state.isLoading, state.error, login, register, logout, refreshToken, clearError]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};